import { createSlice } from "@reduxjs/toolkit";
import {
  calculateOrderTotal,
  createOrder,
  fetchMyOrders,
  fetchOrderDetails,
  searchOrders,
  updateOrderStatus,
} from "../actions/orderActions";

const initialState = {
  myOrders: {
    loading: false,
    error: null,
    data: [],
  },
  selectedOrder: {
    loading: false,
    error: null,
    data: null,
  },
  searchResults: {
    loading: false,
    error: null,
    data: [],
    totalCount: 0,
    pageNumber: 1,
    pageSize: 100,
  },
  createOrder: {
    loading: false,
    error: null,
    success: false,
  },
  orderTotal: {
    loading: false,
    error: null,
    data: null,
  },
  cart: {
    items: [],
    totalAmount: 0,
  },
};

const orderSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    clearSelectedOrder: (state) => {
      state.selectedOrder.data = null;
      state.selectedOrder.error = null;
    },
    clearSearchResults: (state) => {
      state.searchResults.data = [];
      state.searchResults.error = null;
      state.searchResults.totalCount = 0;
    },
    clearCreateOrderState: (state) => {
      state.createOrder.error = null;
      state.createOrder.success = false;
    },
    addToCart: (state, action) => {
      const item = action.payload;
      const existingItem = state.cart.items.find(
        (i) => i.productId === item.productId
      );
      if (existingItem) {
        existingItem.quantity += item.quantity;
      } else {
        state.cart.items.push(item);
      }
      state.cart.totalAmount = state.cart.items.reduce(
        (sum, i) => sum + i.price * i.quantity,
        0
      );
    },
    removeFromCart: (state, action) => {
      const productId = action.payload;
      state.cart.items = state.cart.items.filter(
        (i) => i.productId !== productId
      );
      state.cart.totalAmount = state.cart.items.reduce(
        (sum, i) => sum + i.price * i.quantity,
        0
      );
    },
    updateCartItemQuantity: (state, action) => {
      const { productId, quantity } = action.payload;
      const item = state.cart.items.find((i) => i.productId === productId);
      if (item) {
        item.quantity = quantity;
        state.cart.totalAmount = state.cart.items.reduce(
          (sum, i) => sum + i.price * i.quantity,
          0
        );
      }
    },
    clearCart: (state) => {
      state.cart.items = [];
      state.cart.totalAmount = 0;
    },
  },
  extraReducers: {
    // Get My Orders
    [fetchMyOrders.pending]: (state) => {
      state.myOrders.loading = true;
      state.myOrders.error = null;
    },
    [fetchMyOrders.fulfilled]: (state, action) => {
      state.myOrders.loading = false;
      state.myOrders.data = action.payload.data || [];
    },
    [fetchMyOrders.rejected]: (state, action) => {
      state.myOrders.loading = false;
      state.myOrders.error = action.payload;
    },

    // Get Order Details
    [fetchOrderDetails.pending]: (state) => {
      state.selectedOrder.loading = true;
      state.selectedOrder.error = null;
    },
    [fetchOrderDetails.fulfilled]: (state, action) => {
      state.selectedOrder.loading = false;
      state.selectedOrder.data = action.payload.data;
    },
    [fetchOrderDetails.rejected]: (state, action) => {
      state.selectedOrder.loading = false;
      state.selectedOrder.error = action.payload;
    },

    // Create Order
    [createOrder.pending]: (state) => {
      state.createOrder.loading = true;
      state.createOrder.error = null;
      state.createOrder.success = false;
    },
    [createOrder.fulfilled]: (state, action) => {
      state.createOrder.loading = false;
      state.createOrder.success = true;
      // Clear cart after successful order
      state.cart.items = [];
      state.cart.totalAmount = 0;
    },
    [createOrder.rejected]: (state, action) => {
      state.createOrder.loading = false;
      state.createOrder.error = action.payload;
      state.createOrder.success = false;
    },

    // Update Order Status
    [updateOrderStatus.fulfilled]: (state, action) => {
      // Update order in myOrders list
      const updatedOrder = action.payload.data;
      const index = state.myOrders.data.findIndex(
        (o) => o.id === updatedOrder.id
      );
      if (index !== -1) {
        state.myOrders.data[index] = updatedOrder;
      }
      // Update selected order if it matches
      if (state.selectedOrder.data?.id === updatedOrder.id) {
        state.selectedOrder.data = updatedOrder;
      }
    },

    // Search Orders
    [searchOrders.pending]: (state) => {
      state.searchResults.loading = true;
      state.searchResults.error = null;
    },
    [searchOrders.fulfilled]: (state, action) => {
      state.searchResults.loading = false;
      state.searchResults.data = action.payload.data?.items || [];
      state.searchResults.totalCount = action.payload.data?.totalCount || 0;
      state.searchResults.pageNumber = action.payload.data?.pageNumber || 1;
      state.searchResults.pageSize = action.payload.data?.pageSize || 10;
    },
    [searchOrders.rejected]: (state, action) => {
      state.searchResults.loading = false;
      state.searchResults.error = action.payload;
    },

    // Calculate Order Total
    [calculateOrderTotal.pending]: (state) => {
      state.orderTotal.loading = true;
      state.orderTotal.error = null;
    },
    [calculateOrderTotal.fulfilled]: (state, action) => {
      state.orderTotal.loading = false;
      state.orderTotal.data = action.payload.data;
    },
    [calculateOrderTotal.rejected]: (state, action) => {
      state.orderTotal.loading = false;
      state.orderTotal.error = action.payload;
    },
  },
});

export const {
  clearSelectedOrder,
  clearSearchResults,
  clearCreateOrderState,
  addToCart,
  removeFromCart,
  updateCartItemQuantity,
  clearCart,
} = orderSlice.actions;

export default orderSlice.reducer;
